import { FC, useCallback, useEffect } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Span } from 'components/helpers';
import { getCatalog as onGetCatalog, reorderCatalog, ShopSlicer } from 'store/shop/ShopActions';
import {
  EmptyStateOptions,
  IShopCatalog,
  IStore2,
  LikeProductSource,
  popupIdsOptions,
  ProductPageSources,
} from 'types';
import './catalogWrapper.scss';
import { isEmptyArray } from 'utils';
import { EmptyState } from 'components/emptyState';
import { PopupSlicer } from 'store/popup/PopupActions';
import { CreateShopSlicer } from 'store/createShop/CreateShopSlicer';
import { ROUTES } from 'constant';
import DragItem from 'components/dragGrid/DragItem';
import CatalogItem from '../../catalogItem/CatalogItem';
import DragGrid from 'components/dragGrid/DragGrid';
import { onProductLikeToggle } from 'store/specificProduct/SpecificProductActions';
import { SpecificProductSlicer } from 'store/specificProduct/SpecificProductSlicer';
import CatalogSkeleton from '../../catalogItem/skeleton/CatalogSkeleton';

const TOTAL_PRODUCTS_SKELETON = 8;

const CatalogWrapper: FC<IShopCatalog> = ({
  products,
  getCatalog,
  showPopup,
  shopId,
  isShopOwner,
  editListing,
  duplicateListing,
  onSaveToggle,
  onProductMove,
  reorderStateCatalog,
  updateProductPageSource,
}) => {
  const navigate = useNavigate();

  const onDropItem = useCallback(
    (productId: string) => {
      if (products) {
        const newIndex = products.findIndex((item) => item.id === productId);

        if (newIndex !== -1) {
          onProductMove({ productId, newIndex });
        }
      }
    },
    [products],
  );

  const onItemMove = useCallback((startIndex: number, newIndex: number) => {
    reorderStateCatalog({ oldIndex: startIndex, newIndex });
  }, []);

  useEffect(() => {
    if (!products) {
      getCatalog({ shopId });
    }
  }, [shopId]);

  const onPreviewButtonClick = (index: number) => {
    if (products) {
      updateProductPageSource({ source: ProductPageSources.Shop });
      navigate(`/${ROUTES.PRODUCT}/${products[index].id}`);
    }
  };

  const onDeleteClick = (productId: string) => {
    showPopup({ id: popupIdsOptions.deleteItemPopup, productId });
  };

  const onEditClick = (productId: string) => {
    navigate(`/${ROUTES.EDIT_SHOP}`);
    editListing({ id: productId, shopId });
  };

  const onDuplicateClick = (productId: string) => {
    navigate(`/${ROUTES.EDIT_SHOP}`);
    duplicateListing({ id: productId, shopId });
  };

  const onSaveClick = (productId: string, isLiked: boolean) => {
    onSaveToggle({ productId, isLiked, source: LikeProductSource.Shop });
  };

  const renderList = useCallback(() => {
    if (!products) {
      return [...new Array(TOTAL_PRODUCTS_SKELETON)].map((_, index) => (
        <CatalogSkeleton key={index} />
      ));
    }
    if (isEmptyArray(products)) {
      return <EmptyState type={EmptyStateOptions.ShopCatalog} />;
    }

    return products?.map((item, index) => (
      <DragItem
        key={item.id}
        index={index}
        onItemMove={onItemMove}
        id={item.id}
        onItemDrop={() => onDropItem(item.id)}
        maxRange={products.length - 1}
      >
        <CatalogItem
          id={item.id}
          index={index}
          image={item.images[0]}
          price={item.price}
          originalPrice={item.originalPrice}
          currencyCode={item.currencyCode}
          isDigital={item.isDigital}
          totalLikes={item.totalLikes}
          isLiked={item.isLiked}
          name={item.title}
          quantity={item.quantity}
          shippingInformation={item.shippingInformation}
          onLikeToggle={() => onSaveClick(item.id, item.isLiked)}
          onClick={() => onPreviewButtonClick(index)}
          isShopOwner={isShopOwner}
          onDeleteClick={() => onDeleteClick(item.id)}
          onDuplicateClick={() => onDuplicateClick(item.id)}
          onEditClick={() => onEditClick(item.id)}
          isDraggable
        />
      </DragItem>
    ));
  }, [products]);

  return (
    <div className="shop-page-content">
      <div className="section">
        <Span text="All catalog" className="title" />
        <div className="section-content">
          <div className="catalog">
            <DragGrid>{renderList()}</DragGrid>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: IStore2) => {
  const { fullCatalog: products, details } = state.shop;

  const shopId = details?.id || '';

  return {
    shopId,
    products,
  };
};

const mapDispatchToProps = {
  getCatalog: onGetCatalog,
  onSaveToggle: onProductLikeToggle,
  onProductMove: reorderCatalog,
  reorderStateCatalog: ShopSlicer.actions.reorderCatalog,
  showPopup: PopupSlicer.actions.show,
  editListing: CreateShopSlicer.actions.navigateToEditListingForm,
  duplicateListing: CreateShopSlicer.actions.navigateToDuplicateListingForm,
  updateProductPageSource: SpecificProductSlicer.actions.updateSource,
};

export default connect(mapStateToProps, mapDispatchToProps)(CatalogWrapper);
